import { _initialActivity } from '../../../fixtures/activityFixtures'
import { IPagination } from '../../../types/pagination'
import { statusColor } from '../../../utils/statusColor'
import { _initialDirectCost } from '../../project/reducers/directAndPassTroughCostsReducer'
import { remainingTime } from '../../project/selectors/activitiesSelector'
import { IDirectCost } from '../../project/types/directAndPassTroughCosts'
import { IProjectCurrentDirectCosts, IProjectCurrentTasks } from '../../project/types/project'
import { IProjectActivity } from '../../project/types/projectActivity'
import { _initialEmployee } from '../../settings/reducers/employees'
import { IEmployee } from '../../settings/types/employee'
import * as constants from '../actions/constants'
import { TimeTrackingActions } from '../actions/timeTrackingActions'
import { ITimeTracking, ITimeTrackingChart, ITimeTrackingDonutChart, ITimeTrackingInvolvedTaskFilter, ITimeTrackingInvolvedTasks } from '../types/timeTracking'

export const activeTab = (state = 'overview', action: TimeTrackingActions): string => {
  switch (action.type) {
    case constants.SET_ACTIVE_TIMETRACKING_TAB:
      return action.tab

    default:
      return state
  }
}

export const _timeTracking: ITimeTracking = {
  id: '',
  employee: _initialEmployee,
  activity: _initialActivity,
  direct_cost: _initialDirectCost,
  date: '',
  duration: '',
  inputDuration: '',
  project_color: '',
  project_name: ''
}

const _timeTrackingChart: ITimeTrackingChart = {
  categories: [],
  series: [],
  maximum_hours: 0
}

const _timeTrackingDonutChart: ITimeTrackingDonutChart = {
  labels: [],
  colors: [],
  series: []
}

const _initialCurrentTasksPagination: IPagination<IProjectActivity[]> = {
  links: { next: '', previous: '' },
  count: 0,
  current_page: 0,
  total_pages: 0,
  results: [],
  page_size: 5,
  last_page: 0,
  to: 0,
  from: 0
}

const _initialCurrentDCPagination: IPagination<IDirectCost[]> = {
  links: { next: '', previous: '' },
  count: 0,
  current_page: 0,
  total_pages: 0,
  results: [],
  page_size: 5,
  last_page: 0,
  to: 0,
  from: 0
}

export const _initialProjectTimeTrackingInvolvedTasks: IPagination<ITimeTrackingInvolvedTasks[]> = {
  links: { next: '', previous: '' },
  count: 0,
  current_page: 0,
  total_pages: 0,
  results: [],
  page_size: 11,
  last_page: 0,
  to: 0,
  from: 0
}

const addInputDuration = (duration: string | number): string => {
  const splitDuration = duration.toString().split(':')
  return `${splitDuration[0]}h ${splitDuration[1]}m`
}

export const timeTrackingEntries = (state: ITimeTracking[] = [_timeTracking], action: TimeTrackingActions): ITimeTracking[] => {
  switch (action.type) {
    case constants.GET_TIME_TRACKING_ENTRIES_SUCCESS:
    case constants.GET_ADMIN_TIME_TRACKING_ENTRIES_SUCCESS:
      return action.timeTrackingEntries.map(t => {
        return {...t, inputDuration: addInputDuration(t.duration)}
      })

    case constants.UPDATE_TIME_TRACKING_ENTRY_SUCCESS:
    case constants.UPDATE_ADMIN_TIME_TRACKING_ENTRY_SUCCESS:
      return state.map(entry => {
        if (entry.id === action.timeTrackingEntry.id) {
          return {...action.timeTrackingEntry , inputDuration: addInputDuration(action.timeTrackingEntry.duration)}
        }
        return entry
      })

    case constants.CREATE_TIME_TRACKING_ENTRY_SUCCESS:
    case constants.CREATE_ADMIN_TIME_TRACKING_ENTRY_SUCCESS: {
      const entries = state.map(entry => entry)
      const _createdEntry = {...action.timeTrackingEntry , inputDuration: addInputDuration(action.timeTrackingEntry.duration)}
      entries.push(_createdEntry)
      return entries
    }

    default:
      return state
  }
}

export const timeTrackingEntry = (state: ITimeTracking = _timeTracking, action: TimeTrackingActions): ITimeTracking => {
  switch (action.type) {
    case constants.UPDATE_TIME_TRACKING_ENTRY_SUCCESS:
      return {...action.timeTrackingEntry , inputDuration: addInputDuration(action.timeTrackingEntry.duration)}

    case constants.CREATE_TIME_TRACKING_ENTRY_SUCCESS:
      return {...action.timeTrackingEntry , inputDuration: addInputDuration(action.timeTrackingEntry.duration)}

    case constants.DELETE_TIME_TRACKING_ENTRY_SUCCESS:
      return _timeTracking

    default:
      return state
  }
}

export const timeTrackingInvolvedTasks = (state: ITimeTrackingInvolvedTasks[] = [], action: TimeTrackingActions): ITimeTrackingInvolvedTasks[] => {
  switch (action.type) {
    case constants.GET_TIME_TRACKING_INVOLVED_TASKS_SUCCESS:
      return action.timeTrackingInvolvedTasks

      default:
      return state
  }
}

export const filterTimeTrackingInvolvedTasks = (state: ITimeTrackingInvolvedTaskFilter = {show_all: false}, action: TimeTrackingActions): ITimeTrackingInvolvedTaskFilter => {
  switch (action.type) {
    case constants.FILTER_TIME_TRACKING_INVOLVED_TASKS:
      return action.filters

    default:
      return state
  }
}

export const timeTrackingChartEntry = (state: ITimeTrackingChart = _timeTrackingChart, action: TimeTrackingActions): ITimeTrackingChart => {
  switch (action.type) {
    case constants.GET_TIME_TRACKING_CHART_DATA_SUCCESS:
      return action.timeTrackingChart

    default:
      return state
  }
}

export const timeTrackingDonutChartEntry = (state: ITimeTrackingDonutChart = _timeTrackingDonutChart, action: TimeTrackingActions): ITimeTrackingDonutChart => {
  switch (action.type) {
    case constants.GET_TIME_TRACKING_CHART_DATA_DONUT_SUCCESS:
      return action.timeTrackingDonutChart

    default:
      return state
  }
}

const addFieldsToCurrentTasks = (acts: IProjectActivity[]) => {
  const _acts = acts.map(act => {
    return {
      ...act,
      current_hours: +act.current_hours,
      total_hours: +act.total_hours,
      statusColor: (status: string) => statusColor(status),
      remainingHours: remainingTime(act.current_hours, act.total_hours)[0],
      remainingHoursPercentage: remainingTime(act.current_hours, act.total_hours)[1]
    }
  })
  return _acts
}

const addFieldsToDCTasks = (dcs: IDirectCost[]) => {
  const _dcs = dcs.map(dc => {
    return {
      ...dc,
      current_hours: +dc.current_hours,
      total_hours: +dc.total_hours,
      statusColor: (status: string) => statusColor(status),
      remainingHours: remainingTime(+dc.current_hours, dc.new_total_hours ? dc.new_total_hours : +dc.total_hours)[0],
      remainingHoursPercentage: remainingTime(+dc.current_hours, dc.new_total_hours ? dc.new_total_hours : +dc.total_hours)[1]
    }
  })
  return _dcs
}

export const currentTasks = (state: IProjectCurrentTasks = { activities: [], employees: [] }, action: TimeTrackingActions): IProjectCurrentTasks => {
  switch (action.type) {
    case constants.GET_CURRENT_TASKS_SUCCESS:
      return {
        activities: addFieldsToCurrentTasks(action.data.activities),
        employees: action.data.employees
      }

    default:
      return state
  }
}

export const currentTasksPagination = (state: IPagination<IProjectActivity[]> = _initialCurrentTasksPagination, action: TimeTrackingActions): IPagination<IProjectActivity[]> => {
  switch (action.type) {
    case constants.GET_PAGINATED_CURRENT_TASKS_SUCCESS:
      action.data.results = addFieldsToCurrentTasks(action.data.results)
      return action.data

    default:
      return state
  }
}

export const currentDirectCosts = (state: IProjectCurrentDirectCosts = { direct_costs: [], employees: [] }, action: TimeTrackingActions): IProjectCurrentDirectCosts => {
  switch (action.type) {
    case constants.GET_CURRENT_DIRECT_COSTS_SUCCESS:
      return {
        direct_costs: addFieldsToDCTasks(action.data.direct_costs),
        employees: action.data.employees
      }

    default:
      return state
  }
}

export const currentDirectCostsPagination = (state: IPagination<IDirectCost[]> = _initialCurrentDCPagination, action: TimeTrackingActions): IPagination<IDirectCost[]> => {
  switch (action.type) {
    case constants.GET_PAGINATED_CURRENT_DIRECT_COSTS_SUCCESS:
      action.data.results = addFieldsToDCTasks(action.data.results)
      return action.data

    default:
      return state
  }
}

export const paginatedCurrentTasksActivePage = (state = 1, action: TimeTrackingActions): number => {
  switch (action.type) {
    case constants.SET_PAGINATED_CURRENT_TASKS_ACTIVE_PAGE:
      return action.page

    default:
      return state
  }
}

export const currentTasksStatusFilter = (state = 'in_progress', action: TimeTrackingActions): string => {
  switch (action.type) {
    case constants.SET_CURRENT_TASKS_STATUS_FILTER:
      return action.status

    default:
      return state
  }
}


export const employee = (state: IEmployee | null = null, action: TimeTrackingActions): IEmployee | null => {
  switch (action.type) {
    case constants.SELECT_EMPLOYEE_FOR_TIME_TRACKING:
      return action.employee

    default:
      return state
  }
}

export const paginatedTimeTrackingInvolvedTasks = (state: IPagination<ITimeTrackingInvolvedTasks[]> = _initialProjectTimeTrackingInvolvedTasks, action: TimeTrackingActions ): IPagination<ITimeTrackingInvolvedTasks[]> => {
  switch (action.type) {
    case constants.GET_PAGINATED_TIME_TRACKING_INVOLVED_TASKS_SUCCESS:
      return action.data

    default:
      return state
  }
}

export const paginatedProjectTimeTrackingInvolvedTasksActivePage = (state = 1, action: TimeTrackingActions): number => {
  switch (action.type) {
    case constants.SET_PAGINATED_TIME_TRACKING_INVOLVED_TASKS_ACTIVE_PAGE:
      return action.page

    default:
      return state
  }
}

export const searchProjectTimeTrackingInvolvedTasks = (state: Record<string, string> = { search: '' }, action: TimeTrackingActions): Record<string, string>  => {
  switch (action.type) {
    case constants.SEARCH_PROJECT_TIME_TRACKING_INVOLVED_TASKS:
      return action.queryParams

    default:
      return state
  }
}

export const sortTimeTrackingInvolvedTasksReport = (state: { name: string, direction: string} = { name: '', direction: ''}, action: TimeTrackingActions): { name: string, direction: string } => {
  switch (action.type) {
    case constants.SORT_PROJECT_TIME_TRACKING_INVOLVED_TASKS_REPORT:
      return action.sort

    default:
      return state
  }
}
